import { EquipmentProofLink } from "@/components/oprema/EquipmentProofLink";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/lib/api";
import {
  equipmentApi,
  type HandoverNotice as Notice,
} from "@/lib/oprema";

export function HandoverNotice({
  notice,
  onUndone,
}: {
  notice: Notice;
  onUndone: () => void;
}) {
  const { can } = useAuth();
  const [now, setNow] = useState(Date.now);
  const [busy, setBusy] = useState(false);
  const [undone, setUndone] = useState(false);
  const [error, setError] = useState("");
  const running = useRef(false);
  const deadline = Date.parse(notice.receipt.undo_until);
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000)) || 0;
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);
  async function undo() {
    if (running.current || Date.now() >= deadline) return;
    running.current = true;
    setBusy(true);
    setError("");
    try {
      await equipmentApi.undo(notice.workerId, notice.receipt.operation_id);
      setUndone(true);
      onUndone();
    } catch (cause) {
      setError(errorMessage(cause, "Poništenje nije uspjelo."));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  const message = {
    pocetno_stanje: "Početno stanje je upisano.",
    izdavanje: "Oprema izdana. Potvrda je u kartonu.",
    zamjena: "Oprema je zamijenjena. Potvrda je u kartonu.",
    vracanje: "Oprema je razdužena. Potvrda je u kartonu.",
  }[notice.receipt.vrsta];
  return (
    <div className="space-y-3 rounded-lg border border-green-300 bg-green-50 p-4">
      <p role="status">{undone ? "Primopredaja je poništena." : message}</p>
      {error && <p role="alert">{error}</p>}
      {!undone && (
        <div className="flex flex-wrap items-center gap-3">
          {can("oprema", "create") && (
            <>
              <Button
                className="min-h-11"
                variant="outline"
                disabled={busy || !remaining}
                onClick={() => void undo()}
              >
                {busy
                  ? "Poništavanje…"
                  : `Poništi${remaining ? ` (${remaining} s)` : ""}`}
              </Button>
              {!remaining && <p>Vrijeme za poništavanje je isteklo.</p>}
            </>
          )}
          {notice.receipt.dokument_id && (can("dokument", "read") || can("oprema", "read")) && (
            <EquipmentProofLink
              className="inline-flex min-h-11 items-center underline"
              workerId={notice.workerId} documentId={notice.receipt.dokument_id}
            >
              Otvori potvrdu
            </EquipmentProofLink>
          )}
        </div>
      )}
    </div>
  );
}

/** The result survives the return to either source page without persistent personal data. */
export function ReturnedHandoverNotice({ onUndone }: { onUndone: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { handover?: Notice } | null;
  const [notice] = useState<Notice | null>(state?.handover ?? null);
  useEffect(() => {
    if (state?.handover) {
      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: null,
      });
    }
  }, [state, navigate, location.pathname, location.search, location.hash]);
  return notice ? (
    <HandoverNotice
      key={notice.receipt.operation_id}
      notice={notice}
      onUndone={onUndone}
    />
  ) : null;
}
